import { useCallback, useMemo, useRef, useState } from "react";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
} from "@common/types/taskTemplate";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { AddTaskResult } from "./AddCloseTemplateTaskDrawer";
import { EditTaskResult } from "./EditCloseTemplateTaskDrawer";
import { stageKey } from "./closeTemplateTasksDrawer.constants";
import {
    applyTaskLinks,
    deleteTasksWithLinks,
    getActiveBlockers,
} from "./closeTemplateTaskLinks";
import { LocalTask } from "./closeTemplateTasksDrawer.types";
import { CloseTemplateTaskRow } from "./closeTemplateTasksViewConfig";

interface UseCloseTemplateTaskCallbacksParams {
    localTasks: LocalTask[];
    onLocalTasksChange: (updater: (prev: LocalTask[]) => LocalTask[]) => void;
    sortedStages: OrganizationCloseStage[];
    knownStageIds: Set<string>;
    search: string;
}

export function useCloseTemplateTaskCallbacks({
    localTasks,
    onLocalTasksChange,
    sortedStages,
    knownStageIds,
    search,
}: UseCloseTemplateTaskCallbacksParams) {
    const localTasksRef = useRef(localTasks);
    localTasksRef.current = localTasks;

    const [addTaskStageId, setAddTaskStageId] = useState<
        string | null | undefined
    >(undefined);
    const isAddTaskDrawerOpen = addTaskStageId !== undefined;
    const [editingTask, setEditingTask] = useState<LocalTask | null>(null);
    const [whitelistedStageIds, setWhitelistedStageIds] = useState<Set<string>>(
        new Set(),
    );

    const activeTasks = useMemo(
        () => localTasks.filter((t) => !t.isDeleted),
        [localTasks],
    );

    const activeTasksById = useMemo(
        () => new Map(activeTasks.map((task) => [task.localId, task])),
        [activeTasks],
    );

    const tableRows = useMemo<CloseTemplateTaskRow[]>(() => {
        const visibleTasks = localTasks
            .filter(
                (task) =>
                    !task.isDeleted &&
                    task.name.toLowerCase().includes(search.toLowerCase()),
            )
            .sort((a, b) => a.order - b.order);

        return [...sortedStages, null as OrganizationCloseStage | null]
            .filter((stage) => {
                if (stage !== null && whitelistedStageIds.has(stage.id)) {
                    return true;
                }
                if (stage === null) {
                    return activeTasks.some(
                        (task) =>
                            task.closeStageId === null ||
                            !knownStageIds.has(task.closeStageId ?? ""),
                    );
                }
                return activeTasks.some(
                    (task) => task.closeStageId === stage.id,
                );
            })
            .map((stage) => {
                const key = stageKey(stage?.id ?? null);

                const children: CloseTemplateTaskRow[] = visibleTasks
                    .filter((task) =>
                        stage
                            ? task.closeStageId === stage.id
                            : task.closeStageId === null ||
                              !knownStageIds.has(task.closeStageId ?? ""),
                    )
                    .map((task) => ({
                        id: task.localId,
                        name: task.name,
                        blockedBy: getActiveBlockers(task, activeTasksById),
                        closeStageId: task.closeStageId,
                        assigneeRole: task.assigneeRole,
                        taskType: task.taskType,
                        externalDeadline: task.externalDeadline,
                        deadlineAnchor: task.deadlineAnchor,
                        estimatedTime: task.estimatedTime,
                    }));

                return {
                    id: key,
                    name: stage?.name ?? "No stage",
                    closeStageId: stage?.id ?? null,
                    assigneeRole: null,
                    taskType: null,
                    externalDeadline: null,
                    deadlineAnchor: null,
                    estimatedTime: null,
                    isGroupRow: true,
                    children,
                };
            });
    }, [
        localTasks,
        activeTasks,
        activeTasksById,
        sortedStages,
        knownStageIds,
        search,
        whitelistedStageIds,
    ]);

    const activeTaskCountByStageKey = useMemo(() => {
        const map = new Map<string, number>();
        for (const task of activeTasks) {
            const key = stageKey(
                task.closeStageId !== null &&
                    knownStageIds.has(task.closeStageId)
                    ? task.closeStageId
                    : null,
            );
            map.set(key, (map.get(key) ?? 0) + 1);
        }
        return map;
    }, [activeTasks, knownStageIds]);

    const hiddenStages = useMemo(
        () =>
            sortedStages.filter((stage) => {
                if (whitelistedStageIds.has(stage.id)) {
                    return false;
                }
                return !activeTasks.some((t) => t.closeStageId === stage.id);
            }),
        [sortedStages, activeTasks, whitelistedStageIds],
    );

    const handleAddStageToWhitelist = useCallback((stageId: string) => {
        setWhitelistedStageIds((prev) => new Set([...prev, stageId]));
    }, []);

    const handleRemoveStageFromWhitelist = useCallback((stageId: string) => {
        setWhitelistedStageIds(
            (prev) => new Set([...prev].filter((id) => id !== stageId)),
        );
    }, []);

    const handleRemoveTask = useCallback(
        (localId: string) => {
            onLocalTasksChange((prev) =>
                deleteTasksWithLinks(prev, new Set([localId])),
            );
        },
        [onLocalTasksChange],
    );

    const handleEditTask = useCallback((localId: string) => {
        const task =
            localTasksRef.current.find((t) => t.localId === localId) ?? null;
        setEditingTask(task);
    }, []);

    const handleSaveEditedTask = useCallback(
        (localId: string, result: EditTaskResult) => {
            onLocalTasksChange((prev) => {
                const updated = prev.map((task) =>
                    task.localId === localId
                        ? {
                              ...task,
                              name: result.name,
                              closeStageId: result.closeStageId,
                              assigneeRole: result.assigneeRole,
                              assigneeId: result.assigneeId,
                              externalDeadline: result.externalDeadline,
                              deadlineAnchor: result.deadlineAnchor,
                              estimatedTime: result.estimatedTime,
                              initialMessage: result.initialMessage,
                              viewConfig: result.viewConfig,
                              taskType: result.taskType,
                              sourceTemplateVersion:
                                  result.sourceTemplateVersion,
                          }
                        : task,
                );
                return result.blockedByLocalIds && result.relatedLocalIds
                    ? applyTaskLinks(updated, localId, {
                          blockedByLocalIds: result.blockedByLocalIds,
                          relatedLocalIds: result.relatedLocalIds,
                      })
                    : updated;
            });
            setEditingTask(null);
        },
        [onLocalTasksChange],
    );

    const handleUpdateAssigneeRole = useCallback(
        (localId: string, assigneeRole: TaskTemplateAssigneeRole | null) => {
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    task.localId === localId ? { ...task, assigneeRole } : task,
                ),
            );
        },
        [onLocalTasksChange],
    );

    const handleUpdateCloseStage = useCallback(
        (localId: string, closeStageId: string | null) => {
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    task.localId === localId ? { ...task, closeStageId } : task,
                ),
            );
        },
        [onLocalTasksChange],
    );

    const handleUpdateDeadline = useCallback(
        (localId: string, externalDeadline: number | null) => {
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    task.localId === localId
                        ? { ...task, externalDeadline }
                        : task,
                ),
            );
        },
        [onLocalTasksChange],
    );

    const handleUpdateDeadlineAnchor = useCallback(
        (
            localId: string,
            deadlineAnchor: TaskTemplateDeadlineAnchor | null,
        ) => {
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    task.localId === localId
                        ? { ...task, deadlineAnchor }
                        : task,
                ),
            );
        },
        [onLocalTasksChange],
    );

    const handleUpdateEstimatedTime = useCallback(
        (localId: string, estimatedTime: number | null) => {
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    task.localId === localId
                        ? { ...task, estimatedTime }
                        : task,
                ),
            );
        },
        [onLocalTasksChange],
    );

    const handleOpenAddTaskDrawer = useCallback(() => {
        setAddTaskStageId(null);
    }, []);

    const handleAddTaskForStage = useCallback((closeStageId: string | null) => {
        setAddTaskStageId(closeStageId);
    }, []);

    const handleAddTaskFromDrawer = useCallback(
        (result: AddTaskResult) => {
            onLocalTasksChange((prev) => {
                const nonDeletedTasks = prev.filter((t) => !t.isDeleted);
                const nextOrder =
                    nonDeletedTasks.length > 0
                        ? Math.max(...nonDeletedTasks.map((t) => t.order)) + 1
                        : 0;
                const withNewTask = [
                    ...prev,
                    {
                        localId: result.localId,
                        persistedId: undefined,
                        name: result.name,
                        closeStageId: result.closeStageId,
                        assigneeRole: result.assigneeRole,
                        assigneeId: result.assigneeId,
                        externalDeadline: result.externalDeadline,
                        deadlineAnchor: result.deadlineAnchor,
                        estimatedTime: result.estimatedTime,
                        taskType: result.taskType,
                        initialMessage: result.initialMessage,
                        viewConfig: result.viewConfig,
                        sourceTemplateId: result.sourceTemplateId,
                        sourceTemplateVersion: null,
                        isDeleted: false,
                        order: nextOrder,
                        blockedByLocalIds: [],
                        relatedLocalIds: [],
                    },
                ];
                return result.blockedByLocalIds && result.relatedLocalIds
                    ? applyTaskLinks(withNewTask, result.localId, {
                          blockedByLocalIds: result.blockedByLocalIds,
                          relatedLocalIds: result.relatedLocalIds,
                      })
                    : withNewTask;
            });
            setAddTaskStageId(undefined);
        },
        [onLocalTasksChange],
    );

    const handleCloseAddTaskDrawer = useCallback(() => {
        setAddTaskStageId(undefined);
    }, []);

    const handleCloseEditTaskDrawer = useCallback(() => {
        setEditingTask(null);
    }, []);

    return {
        tableRows,
        addTaskStageId,
        isAddTaskDrawerOpen,
        editingTask,
        hiddenStages,
        whitelistedStageIds,
        activeTaskCountByStageKey,
        handleAddStageToWhitelist,
        handleRemoveStageFromWhitelist,
        handleRemoveTask,
        handleEditTask,
        handleSaveEditedTask,
        handleUpdateAssigneeRole,
        handleUpdateCloseStage,
        handleUpdateDeadline,
        handleUpdateDeadlineAnchor,
        handleUpdateEstimatedTime,
        handleOpenAddTaskDrawer,
        handleAddTaskForStage,
        handleAddTaskFromDrawer,
        handleCloseAddTaskDrawer,
        handleCloseEditTaskDrawer,
    };
}
